import { useEffect, type ReactNode } from 'react';
import { useThemeStore } from '@app/theme/theme.store';

interface ThemeProviderProps {
  children: ReactNode;
}

export function ThemeProvider({ children }: ThemeProviderProps) {
  const mode = useThemeStore((s) => s.mode);
  const hasUserPreference = useThemeStore((s) => s.hasUserPreference);
  const setMode = useThemeStore((s) => s.setMode);

  // Apply theme to <html data-theme="…">
  useEffect(() => {
    const root = document.documentElement;
    root.setAttribute('data-theme', mode);
    const themeColor = mode === 'dark' ? '#0e0e0c' : '#faf8f3';
    const meta = document.querySelector('meta[name="theme-color"]');
    if (meta) meta.setAttribute('content', themeColor);
  }, [mode]);

  // Follow system theme until the user explicitly toggles.
  useEffect(() => {
    if (hasUserPreference) return;
    const mq = window.matchMedia('(prefers-color-scheme: light)');
    const handler = (e: MediaQueryListEvent) => {
      const next = e.matches ? 'light' : 'dark';
      // Direct set without flipping hasUserPreference.
      useThemeStore.setState({ mode: next });
    };
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
    // setMode is stable; we just want to track hasUserPreference.
  }, [hasUserPreference, setMode]);

  return <>{children}</>;
}
